// WWI supply depot — the quartermaster's yard. An open-sided storage shed on
// timber posts fills the back of the plot under a big hazard-yellow gable (the
// logistics code, the identity from above), stacked with crates; the front
// yard is open ground with the loading crate stack, ration sacks and a cluster
// of fuel drums where the hauler bends and loads.
import * as THREE from "three";
import { defineModel, leanLoad, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, ROOF_LOGI } = styleV3;

// Muted WWI materials.
const KHAKI = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TIMBER_W = "#8b6438";
const MUD = "#5d4a33";
const CANVAS_W = "#cbbd93";

const BASE = 0.12;                 // foundation slab top
const SHED_Z = -0.5;               // shed centre z (back half of the plot)
const SHED_W = 1.7, SHED_D = 0.85;
const POST_H = 0.62;               // shed post height
const STACK_X = 0.45, STACK_Z = 0.42;   // the loading crate stack in the yard

// A supply crate: timber box with a khaki stencil band.
function crate(x: number, y: number, z: number, s: number, yaw = 0): Part[] {
  return [
    box(s, s * 0.75, s, TIMBER_W, [x, y + s * 0.375, z], [0, yaw, 0]),
    box(s + 0.015, s * 0.16, s + 0.015, KHAKI, [x, y + s * 0.375, z], [0, yaw, 0]),
  ];
}

// A ration sack: a squashed khaki-canvas sphere.
function sack(x: number, z: number, r: number, yaw = 0): Part {
  return {
    geometry: new THREE.SphereGeometry(r, 7, 5), color: CANVAS_W,
    position: [x, BASE + r * 0.62, z], rotation: [0, yaw, 0], scale: [1.1, 0.62, 0.9],
  };
}

// A fuel drum: a gunmetal cylinder with a mud rib.
function drum(x: number, z: number, lying = false): Part[] {
  if (lying) {
    return [
      cyl(0.09, 0.09, 0.2, 8, GUNMETAL, [x, BASE + 0.09, z], [0, 0, Math.PI / 2]),
    ];
  }
  return [
    cyl(0.09, 0.09, 0.22, 8, GUNMETAL, [x, BASE + 0.11, z]),
    cyl(0.095, 0.095, 0.025, 8, MUD, [x, BASE + 0.11, z]),
  ];
}

// The shed's six timber posts.
function shedPosts(h: number): Part[] {
  const out: Part[] = [];
  for (const x of [-0.78, 0, 0.78]) {
    for (const dz of [-0.36, 0.36]) {
      out.push(cyl(0.035, 0.04, h, 5, TIMBER_W, [x, BASE + h / 2, SHED_Z + dz]));
    }
  }
  return out;
}

// The stock kept under the shed roof.
function shedStock(): Part[] {
  return [
    ...crate(-0.55, BASE, SHED_Z, 0.26),
    ...crate(-0.55, BASE + 0.2, SHED_Z + 0.05, 0.22, 0.3),
    ...crate(-0.15, BASE, SHED_Z - 0.12, 0.24, -0.15),
    ...crate(0.32, BASE, SHED_Z + 0.08, 0.26, 0.1),
    sack(0.68, SHED_Z - 0.1, 0.11, 0.4),
    sack(0.6, SHED_Z + 0.18, 0.1, -0.5),
  ];
}

// The open-yard goods: the loading crate stack, sacks and fuel drums.
function yard(): Part[] {
  return [
    // The loading stack the hauler works at.
    ...crate(STACK_X, BASE, STACK_Z, 0.3),
    ...crate(STACK_X + 0.02, BASE + 0.225, STACK_Z - 0.03, 0.24, 0.35),
    ...crate(STACK_X - 0.28, BASE, STACK_Z + 0.12, 0.22, -0.2),
    // Ration sacks by the stack.
    sack(-0.12, 0.55, 0.11, 0.2),
    sack(-0.3, 0.44, 0.1, -0.4),
    // Fuel drums clustered at the west edge of the yard.
    ...drum(-0.66, 0.35),
    ...drum(-0.8, 0.18),
    ...drum(-0.6, 0.12, true),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), ...shedPosts(0.3), ...poles(1.8, 1.8, 0.7)];
  if (s === 2) {
    return [slab(1.94, 1.94), ...shedPosts(POST_H), ...crate(-0.55, BASE, SHED_Z, 0.26), ...poles(1.8, 1.8, 1.2)];
  }

  return [
    slab(1.94, 1.94),
    ...shedPosts(POST_H),
    // The big hazard-yellow logistics gable over the shed — ONE part.
    gable(SHED_D + 0.2, SHED_W + 0.2, 0.34, ROOF_LOGI, 0, BASE + POST_H, SHED_Z, "x"),
    ...shedStock(),
    ...yard(),
  ];
}

export const supplyDepot = defineModel({
  id: "supply-depot",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Supply Depot",
  tags: ["building", "wwi", "military", "logistics", "storage", "depot"],
  summary: "An open-sided storage shed with a hazard-yellow roof and a loading yard.",
  description:
    "A quartermaster's depot: an open-sided shed under a hazard-yellow gable " +
    "stacked with crated stores, fronted by an open yard of crates, ration " +
    "sacks and fuel drums where haulers load supplies for the front.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => partsFor(0) },
    { id: "lower", label: "Lower", parts: () => partsFor(1) },
    { id: "upper", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "load", position: [STACK_X, 0.12, STACK_Z + 0.35], facing: Math.PI, description: "The loading spot at the yard crate stack." },
  ],
  interactions: [
    { id: "load-supplies", label: "Loading supplies", station: "load", variants: ["complete"], ...leanLoad() },
  ],
});
